import React from 'react';
import { Eye, ExternalLink, Smartphone, Monitor, Tablet, ChevronLeft, ChevronRight } from 'lucide-react';
import { AdPlatformKey } from '../config/adPlatformConfig';

interface AdPreviewProps {
  platform: AdPlatformKey;
  content: any;
  metadata?: any;
}

export default function AdPreview({ platform, content, metadata }: AdPreviewProps) {
  const [deviceMode, setDeviceMode] = React.useState<'desktop' | 'mobile' | 'tablet'>('desktop');

  // State for controlling sliders
  const [currentCreativeIndex, setCurrentCreativeIndex] = React.useState(0);
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = React.useState(0);
  const [currentDescriptionIndex, setCurrentDescriptionIndex] = React.useState(0);
  const [currentPrimaryTextIndex, setCurrentPrimaryTextIndex] = React.useState(0);

  // Get available variations for sliders
  const creatives = content?.creatives || [];
  const headlines = content?.headlines || content?.headlineVariations || (content?.headline ? [content.headline] : []);
  const descriptions = content?.descriptions || content?.descriptionVariations || (content?.description ? [content.description] : []);
  const primaryTexts = content?.primaryTextVariations || (content?.primaryText ? [content.primaryText] : []);

  // Reset indices when content changes
  React.useEffect(() => {
    setCurrentCreativeIndex(0);
    setCurrentHeadlineIndex(0);
    setCurrentDescriptionIndex(0);
    setCurrentPrimaryTextIndex(0);
  }, [content, platform]);

  // Slider component for variations
  const renderSlider = (label: string, items: any[], currentIndex: number, onIndexChange: (index: number) => void, displayProperty?: string) => {
    if (items.length <= 1) return null;

    const displayValue = displayProperty ? items[currentIndex]?.[displayProperty] : items[currentIndex];
    const previewText = typeof displayValue === 'string' ? displayValue : JSON.stringify(displayValue);

    return (
      <div className="bg-gray-100 rounded p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">{label} ({currentIndex + 1}/{items.length})</span>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => onIndexChange(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onIndexChange(Math.min(items.length - 1, currentIndex + 1))}
              disabled={currentIndex === items.length - 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="text-xs text-gray-500 line-clamp-2">
          {previewText.length > 60 ? `${previewText.substring(0, 60)}...` : previewText}
        </div>
      </div>
    );
  };

  const renderGoogleSearchAd = () => {
    const currentHeadline = headlines[currentHeadlineIndex] || 'Headline';
    const currentDescription = descriptions[currentDescriptionIndex] || 'Description';

    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="mb-3 text-xs text-gray-500">Google Search Result</div>

        {/* Ad Label */}
        <div className="inline-block px-2 py-0.5 bg-gray-100 text-xs font-medium rounded mb-2">
          Ad
        </div>

        {/* URL */}
        <div className="flex items-center space-x-1 text-sm mb-2">
          <span className="text-gray-600">{content?.displayUrl || 'www.example.com'}</span>
          <span className="text-gray-400">›</span>
          <span className="text-gray-600">{content?.path1}</span>
          {content?.path2 && (
            <>
              <span className="text-gray-400">›</span>
              <span className="text-gray-600">{content?.path2}</span>
            </>
          )}
        </div>

        {/* Headlines */}
        <h3 className="text-xl text-blue-600 hover:underline cursor-pointer mb-2">
          {currentHeadline}
        </h3>

        {/* Descriptions */}
        <p className="text-sm text-gray-700">
          {currentDescription}
        </p>

        {/* Extensions */}
        {content?.sitelinks && content.sitelinks.length > 0 && (
          <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2">
            {content.sitelinks.slice(0, 4).map((link: any, i: number) => (
              <div key={i} className="text-sm">
                <div className="text-blue-600 hover:underline cursor-pointer">{link.text}</div>
                <div className="text-gray-600 text-xs">{link.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFacebookAd = () => {
    const currentPrimaryText = primaryTexts[currentPrimaryTextIndex] || content?.primaryText || '';
    const currentHeadline = headlines[currentHeadlineIndex] || content?.headline || 'Ad Headline';
    const currentCreative = creatives[currentCreativeIndex];

    return (
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
            <div>
              <div className="font-semibold text-sm">Your Business Name</div>
              <div className="text-xs text-gray-500">Sponsored • <span className="cursor-pointer hover:underline">🌐</span></div>
            </div>
          </div>
        </div>

        {/* Primary Text */}
        {currentPrimaryText && (
          <div className="px-4 pt-3 text-sm text-gray-800">
            {currentPrimaryText}
          </div>
        )}

        {/* Media */}
        <div className="mt-3">
          {currentCreative ? (
            <div className="aspect-video bg-black rounded overflow-hidden">
              {currentCreative.type === 'video' ? (
                <video
                  src={currentCreative.url}
                  className="w-full h-full object-cover"
                  controls
                  muted
                  preload="metadata"
                  poster={currentCreative.thumbnail}
                >
                  <source src={currentCreative.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : currentCreative.type === 'image' ? (
                <img
                  src={currentCreative.url}
                  alt={currentCreative.altText || currentCreative.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                  <span className="text-gray-500">{currentCreative.name || 'Ad Creative'}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="aspect-video bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
              <span className="text-gray-500">No Media Uploaded</span>
            </div>
          )}
        </div>

        {/* Link Card */}
        <div className="p-4 border-t bg-gray-50">
          <div className="text-xs text-gray-500 uppercase mb-1">
            {content?.linkUrl ? new URL(content.linkUrl).hostname : 'EXAMPLE.COM'}
          </div>
          <div className="font-semibold text-sm mb-1">{currentHeadline}</div>
          <div className="text-xs text-gray-600">{content?.description || 'Ad description text'}</div>
        </div>

        {/* CTA Button */}
        <div className="px-4 pb-4 bg-gray-50">
          <button className="w-full py-2 bg-blue-600 text-white rounded font-medium text-sm hover:bg-blue-700">
            {content?.callToAction?.replace('_', ' ').toUpperCase() || 'LEARN MORE'}
          </button>
        </div>

        {/* Engagement Bar */}
        <div className="px-4 py-3 border-t flex items-center justify-between text-gray-600">
          <button className="flex items-center space-x-2 hover:bg-gray-100 px-3 py-1 rounded">
            <span className="text-sm">👍 Like</span>
          </button>
          <button className="flex items-center space-x-2 hover:bg-gray-100 px-3 py-1 rounded">
            <span className="text-sm">💬 Comment</span>
          </button>
          <button className="flex items-center space-x-2 hover:bg-gray-100 px-3 py-1 rounded">
            <span className="text-sm">↗️ Share</span>
          </button>
        </div>
      </div>
    );
  };

  const renderLinkedInAd = () => {
    const currentIntroText = content?.introTextVariations?.[currentPrimaryTextIndex] || content?.introText || '';
    const currentHeadline = headlines[currentHeadlineIndex] || content?.headline || 'Compelling Headline';
    const currentCreative = creatives[currentCreativeIndex];

    return (
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-600 rounded"></div>
            <div className="flex-1">
              <div className="font-semibold text-sm">Company Name</div>
              <div className="text-xs text-gray-500">10,000 followers</div>
              <div className="text-xs text-gray-500">Promoted</div>
            </div>
            <button className="text-gray-500 hover:bg-gray-100 p-1 rounded">
              <span>•••</span>
            </button>
          </div>
        </div>

        {/* Intro Text */}
        {currentIntroText && (
          <div className="px-4 pt-3 text-sm text-gray-800">
            {currentIntroText}
          </div>
        )}

        {/* Media */}
        <div className="mt-3">
          {currentCreative ? (
            <div className="aspect-video bg-black rounded overflow-hidden">
              {currentCreative.type === 'video' ? (
                <video
                  src={currentCreative.url}
                  className="w-full h-full object-cover"
                  controls
                  muted
                  preload="metadata"
                  poster={currentCreative.thumbnail}
                >
                  <source src={currentCreative.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : currentCreative.type === 'image' ? (
                <img
                  src={currentCreative.url}
                  alt={currentCreative.altText || currentCreative.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                  <span className="text-gray-500">{currentCreative.name || 'Professional Ad Content'}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="aspect-video bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
              <span className="text-gray-500">No Media Uploaded</span>
            </div>
          )}
        </div>

        {/* Content Card */}
        <div className="p-4 bg-gray-50 border-t">
          <div className="font-semibold text-sm mb-1">{currentHeadline}</div>
          <div className="text-xs text-gray-600">{content?.description || 'Professional description for your target audience'}</div>
        </div>

        {/* Engagement */}
        <div className="px-4 py-2 border-t text-xs text-gray-500">
          <span>25 reactions • 10 comments</span>
        </div>

        {/* Actions */}
        <div className="px-4 py-2 border-t flex items-center justify-around text-gray-600">
          <button className="flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded">
            <span className="text-sm">👍 Like</span>
          </button>
          <button className="flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded">
            <span className="text-sm">💬 Comment</span>
          </button>
          <button className="flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded">
            <span className="text-sm">🔄 Repost</span>
          </button>
          <button className="flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded">
            <span className="text-sm">📤 Send</span>
          </button>
        </div>
      </div>
    );
  };

  const renderDisplayAd = () => {
    const size = content?.adSizes?.[0] || '300x250';
    const [width, height] = size.split('x').map(Number);
    const currentCreative = creatives[currentCreativeIndex];
    const currentHeadline = headlines[currentHeadlineIndex] || content?.headline || 'Display Ad';
    const currentDescription = descriptions[currentDescriptionIndex] || content?.description || 'Engaging banner content';

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="mb-3 text-xs text-gray-500">Display Banner - {size}</div>

          {currentCreative && currentCreative.type === 'image' ? (
            <div
              className="border rounded overflow-hidden relative"
              style={{ width: `${Math.min(width, 400)}px`, height: `${height * Math.min(400/width, 1)}px` }}
            >
              <img
                src={currentCreative.url}
                alt={currentCreative.altText || currentCreative.name}
                className="w-full h-full object-cover"
              />
              {/* Overlay with headline and CTA */}
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="text-lg font-bold mb-2">
                    {currentHeadline}
                  </div>
                  <button className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm">
                    {content?.callToAction || 'Click Here'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50"
              style={{ width: `${Math.min(width, 400)}px`, height: `${height * Math.min(400/width, 1)}px` }}
            >
              <div className="text-center">
                <div className="text-lg font-bold text-gray-700 mb-2">
                  {currentHeadline}
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  {currentDescription}
                </div>
                <button className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm">
                  {content?.callToAction || 'Click Here'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Show multiple sizes if available */}
        {content?.adSizes?.length > 1 && (
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-xs text-gray-500 mb-2">Other Sizes</div>
            <div className="flex flex-wrap gap-2">
              {content.adSizes.slice(1).map((size: string) => (
                <div key={size} className="px-3 py-1 bg-gray-100 rounded text-xs">
                  {size}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCTVAd = () => {
    const currentCreative = creatives[currentCreativeIndex];
    const currentHeadline = headlines[currentHeadlineIndex] || content?.headline || 'TV Commercial';
    const qrCodes = content?.qrCodes || [];
    const currentQrCode = qrCodes[0]; // Show first QR code if available

    return (
      <div className="bg-black rounded-lg overflow-hidden">
        <div className="aspect-video relative">
          {/* Video Content or Placeholder */}
          {currentCreative && currentCreative.type === 'video' ? (
            <video
              src={currentCreative.url}
              className="w-full h-full object-cover"
              controls
              muted
              preload="metadata"
              poster={currentCreative.thumbnail}
            >
              <source src={currentCreative.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
              <div className="text-center text-white">
                <div className="mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="w-0 h-0 border-l-[20px] border-l-white border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent ml-1"></div>
                  </div>
                </div>
                <div className="text-2xl font-bold mb-2">{currentHeadline}</div>
                <div className="text-sm text-gray-300 mb-4">
                  {content?.videoSpecs?.duration || 30} second spot
                </div>
                <div className="text-xs text-gray-400">
                  {content?.platforms?.join(', ') || 'Connected TV Platforms'}
                </div>
              </div>
            </div>
          )}

          {/* QR Code Overlay */}
          {currentQrCode && (
            <div className="absolute bottom-20 right-8 bg-white rounded-lg p-3 shadow-xl">
              <img
                src={currentQrCode.url}
                alt="QR Code"
                className="w-32 h-32 object-contain"
              />
              <div className="text-xs text-center mt-2 font-semibold text-gray-800">
                Scan for More Info
              </div>
            </div>
          )}

          {/* Skip Ad Button */}
          <div className="absolute bottom-4 right-4 bg-black/50 text-white text-xs px-3 py-1 rounded">
            Skip Ad in 5s
          </div>
        </div>

        {/* Platform Bar */}
        <div className="bg-gray-900 px-4 py-2 flex items-center justify-between">
          <div className="text-white text-sm">Now Playing on {content?.platforms?.[0] || 'Streaming TV'}</div>
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
            <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
          </div>
        </div>
      </div>
    );
  };

  const renderRadioAd = () => {
    const currentHeadline = headlines[currentHeadlineIndex] || content?.headline || 'Audio Advertisement';
    const currentPrimaryText = primaryTexts[currentPrimaryTextIndex] || content?.voiceoverScript || '';
    const companionImages = content?.companionDisplayVariations || [];
    const currentCompanionImage = companionImages[0]; // Show first companion image if available

    return (
      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-20 h-20 bg-white/20 rounded-lg flex items-center justify-center">
            <span className="text-4xl">🎵</span>
          </div>
          <div className="flex-1">
            <div className="text-sm opacity-80 mb-1">{content?.platform || 'Audio Platform'}</div>
            <div className="text-xl font-bold">{currentHeadline}</div>
            <div className="text-sm opacity-80">{content?.audioSpecs?.duration || 30} seconds</div>
          </div>
        </div>

        {/* Audio Waveform Visualization */}
        <div className="bg-white/10 rounded p-4 mb-4">
          <div className="flex items-center justify-center space-x-1 h-16">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="w-1 bg-white/60 rounded"
                style={{ height: `${Math.random() * 100}%` }}
              ></div>
            ))}
          </div>
        </div>

        {/* Script Preview */}
        {currentPrimaryText && (
          <div className="bg-white/10 rounded p-4">
            <div className="text-xs opacity-80 mb-2">SCRIPT</div>
            <div className="text-sm line-clamp-3">
              {currentPrimaryText}
            </div>
          </div>
        )}

        {/* Audio Player */}
        {(() => {
          const currentAudioCreative = creatives.find((c: any) => c.type === 'audio');
          return currentAudioCreative && (
            <div className="bg-white/10 rounded p-4 mb-4">
              <div className="text-xs opacity-80 mb-2">AUDIO PREVIEW</div>
              <audio
                src={currentAudioCreative.url}
                controls
                className="w-full"
                preload="metadata"
              >
                Your browser does not support the audio element.
              </audio>
            </div>
          );
        })()}

        {/* Companion Display */}
        {currentCompanionImage ? (
          <div className="mt-4 bg-white rounded-lg p-4 text-gray-800">
            <div className="text-xs text-gray-500 mb-3 font-semibold">COMPANION DISPLAY</div>
            <div className="flex items-center space-x-4">
              <img
                src={currentCompanionImage.url}
                alt="Companion Display"
                className="w-24 h-24 object-cover rounded-lg shadow-md"
              />
              <div className="flex-1">
                <div className="font-semibold text-base mb-1">{currentCompanionImage.name || 'Visual Element'}</div>
                <div className="text-sm text-gray-600">Displayed during audio playback</div>
                <div className="text-xs text-gray-500 mt-2">
                  {content?.platform === 'spotify' ? 'Spotify Canvas' :
                   content?.platform === 'pandora' ? 'Visual Companion' :
                   content?.platform === 'podcast' ? 'Episode Artwork' :
                   'Companion Image'}
                </div>
              </div>
            </div>
          </div>
        ) : (() => {
          const currentImageCreative = creatives.find((c: any) => c.type === 'image');
          return currentImageCreative ? (
            <div className="mt-4 bg-white rounded-lg p-4 text-gray-800">
              <div className="text-xs text-gray-500 mb-3 font-semibold">COMPANION DISPLAY</div>
              <div className="flex items-center space-x-4">
                <img
                  src={currentImageCreative.url}
                  alt="Companion Display"
                  className="w-24 h-24 object-cover rounded-lg shadow-md"
                />
                <div className="flex-1">
                  <div className="font-semibold text-base mb-1">{currentImageCreative.name || 'Visual Element'}</div>
                  <div className="text-sm text-gray-600">Displayed during audio playback</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 bg-white/10 rounded-lg p-4">
              <div className="text-xs opacity-80 mb-2">COMPANION DISPLAY</div>
              <div className="flex items-center space-x-3">
                <div className="w-20 h-20 bg-white/20 rounded-lg"></div>
                <div className="flex-1">
                  <div className="font-semibold text-sm">No visual uploaded</div>
                  <div className="text-xs opacity-80">Add companion image for better engagement</div>
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    );
  };

  const renderPreview = () => {
    switch (platform) {
      case 'google':
        return renderGoogleSearchAd();
      case 'facebook':
        return renderFacebookAd();
      case 'linkedin':
        return renderLinkedInAd();
      case 'display':
        return renderDisplayAd();
      case 'ctv':
        return renderCTVAd();
      case 'radio':
        return renderRadioAd();
      default:
        return <div>Preview not available</div>;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Device Mode Selector */}
      <div className="bg-gray-50 border-b px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Eye className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Ad Preview</span>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setDeviceMode('desktop')}
              className={`p-2 rounded ${deviceMode === 'desktop' ? 'bg-white shadow' : 'hover:bg-gray-200'}`}
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDeviceMode('tablet')}
              className={`p-2 rounded ${deviceMode === 'tablet' ? 'bg-white shadow' : 'hover:bg-gray-200'}`}
            >
              <Tablet className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDeviceMode('mobile')}
              className={`p-2 rounded ${deviceMode === 'mobile' ? 'bg-white shadow' : 'hover:bg-gray-200'}`}
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-auto p-6 bg-gray-100">
        <div className={`mx-auto ${
          deviceMode === 'mobile' ? 'max-w-sm' :
          deviceMode === 'tablet' ? 'max-w-2xl' :
          'max-w-4xl'
        }`}>
          {/* Preview Sliders */}
          <div className="mb-4">
            {renderSlider('Creatives', creatives, currentCreativeIndex, setCurrentCreativeIndex, 'name')}
            {renderSlider('Headlines', headlines, currentHeadlineIndex, setCurrentHeadlineIndex)}
            {renderSlider('Descriptions', descriptions, currentDescriptionIndex, setCurrentDescriptionIndex)}
            {renderSlider('Primary Text', primaryTexts, currentPrimaryTextIndex, setCurrentPrimaryTextIndex)}
          </div>

          {renderPreview()}
        </div>
      </div>
    </div>
  );
}